import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Faction, Character, Item } from '../../types';
import { GlobalConfirmModal } from '../../components/modals/GlobalConfirmModal';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { dataCache } from '../../cache/DataCache';
import { notifyError, errorMessage } from '../../utils/notify';
import { LoadError } from '../../components/LoadError';
import { CharacterRelationsPanel } from './CharacterRelationsPanel';
import { resolveDataLayout } from './dataLibraryLayout';
import './styles/dataLibrary.css';

// 创建/编辑弹窗遮罩：半透明 + 背景模糊，让背后叠层（编辑器文本/置灰取名窗）整体虚化，聚焦当前弹窗
const FORM_MODAL_MASK: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'var(--modal-mask)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
};

type TabType = 'factions' | 'characters' | 'items';

// =============== SVG 树形图组件 ===============
interface NodePosition {
  id: string;
  x: number;
  y: number;
  name: string;
}

function calculateNodePositions(faction: Faction, allFactions: Faction[], x: number, y: number, gap = 80): NodePosition[] {
  const positions: NodePosition[] = [{ id: faction.id, x, y, name: faction.name }];
  const children = allFactions.filter(f => f.parent_faction_id === faction.id);
  if (children.length === 0) return positions;
  const childWidth = gap * 2;
  const totalWidth = childWidth * children.length;
  const startX = x - totalWidth / 2;
  children.forEach((child, idx) => {
    const childX = startX + childWidth * idx + gap;
    positions.push(...calculateNodePositions(child, allFactions, childX, y + 100, gap));
  });
  return positions;
}

function SvgTreeView({ faction, allFactions, currentFactionId, onSelectFaction }: {
  faction: Faction; allFactions: Faction[]; currentFactionId: string; onSelectFaction: (factionId: string) => void;
}) {
  const positions = calculateNodePositions(faction, allFactions, 400, 40);
  const minX = Math.min(...positions.map(p => p.x));
  const maxX = Math.max(...positions.map(p => p.x));
  const maxY = Math.max(...positions.map(p => p.y));
  const padding = 60;
  const width = maxX - minX + padding * 2;
  const height = maxY + padding;
  const viewBoxMinX = minX - padding;

  const lines: { x1: number; y1: number; x2: number; y2: number }[] = [];
  positions.forEach(node => {
    allFactions.filter(f => f.parent_faction_id === node.id).forEach(child => {
      const childPos = positions.find(p => p.id === child.id);
      if (childPos) {
        lines.push({ x1: node.x, y1: node.y + 40, x2: node.x, y2: node.y + 60 });
        lines.push({ x1: childPos.x, y1: childPos.y, x2: childPos.x, y2: node.y + 60 });
        const midY = node.y + 60;
        lines.push({ x1: Math.min(node.x, childPos.x), y1: midY, x2: Math.max(node.x, childPos.x), y2: midY });
      }
    });
  });

  return (
    <div style={{ overflowX: 'auto', overflowY: 'auto', width: '100%', height: '100%' }}>
      <svg width={width} height={height} style={{ minWidth: width, minHeight: height, display: 'block' }} viewBox={`${viewBoxMinX} 0 ${width} ${height}`}>
        {lines.map((line, idx) => (
          <line key={idx} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke="var(--border-color)" strokeWidth="2" />
        ))}
        {positions.map(pos => (
          <g key={pos.id}>
            <foreignObject x={pos.x - 60} y={pos.y} width="120" height="40">
              <button
                onClick={() => onSelectFaction(pos.id)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: pos.id === currentFactionId ? 'var(--primary-color)' : 'var(--card-bg)',
                  color: pos.id === currentFactionId ? 'white' : 'var(--text-color)',
                  border: `2px solid ${pos.id === currentFactionId ? 'var(--primary-color)' : 'var(--border-color)'}`,
                  borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                  transition: 'all 0.2s', whiteSpace: 'nowrap', width: '100%', height: '100%',
                  boxShadow: pos.id === currentFactionId ? '0 2px 8px rgba(0,0,0,0.2)' : 'none'
                }}
                onMouseEnter={(e) => { if (pos.id !== currentFactionId) { e.currentTarget.style.backgroundColor = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--primary-color)'; } }}
                onMouseLeave={(e) => { if (pos.id !== currentFactionId) { e.currentTarget.style.backgroundColor = 'var(--card-bg)'; e.currentTarget.style.borderColor = 'var(--border-color)'; } }}
              >
                {pos.name}
              </button>
            </foreignObject>
          </g>
        ))}
      </svg>
    </div>
  );
}

// =============== 势力关系树弹窗 ===============
function FactionRelationshipModal({ faction, onClose, onSelectFaction }: {
  faction: Faction; allFactions: Faction[]; onClose: () => void; onSelectFaction: (factionId: string) => void;
}) {
  const [relationshipFactions, setRelationshipFactions] = React.useState<Faction[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [rootFaction, setRootFaction] = React.useState<Faction | null>(null);
  const [scale, setScale] = React.useState(1);
  const treeContainerRef = React.useRef<HTMLDivElement>(null);
  const treeContentRef = React.useRef<HTMLDivElement>(null);

  // Escape 关闭
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  React.useEffect(() => {
    const loadRelationship = async () => {
      try {
        const data = await window.api.getFactionTree(faction.book_id, faction.id);
        setRelationshipFactions(data);
        setRootFaction(data.find(f => f.parent_faction_id === null) || null);
      } catch {
        // faction tree load failure is non-critical
      } finally {
        setLoading(false);
      }
    };
    loadRelationship();
  }, [faction]);

  React.useEffect(() => {
    if (treeContentRef.current && treeContainerRef.current) {
      setTimeout(() => {
        const contentWidth = treeContentRef.current?.scrollWidth || 0;
        const contentHeight = treeContentRef.current?.scrollHeight || 0;
        const containerWidth = treeContainerRef.current?.clientWidth || 0;
        const containerHeight = treeContainerRef.current?.clientHeight || 0;
        const scaleX = (containerWidth * 0.9) / contentWidth;
        const scaleY = (containerHeight * 0.9) / contentHeight;
        setScale(Math.max(Math.min(scaleX, scaleY, 1), 0.3));
      }, 100);
    }
  }, [relationshipFactions, loading]);

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--modal-mask)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
      <div style={{ backgroundColor: 'var(--card-bg)', padding: 20, borderRadius: 8, width: 'min(90vw, 1200px)', maxHeight: '90vh', boxShadow: '0 4px 6px var(--shadow-color)', color: 'var(--text-color)', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>加载中...</div>
        ) : !rootFaction || relationshipFactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>该势力无关系树数据</div>
        ) : (
          <div ref={treeContainerRef} style={{ backgroundColor: 'var(--bg-color)', borderRadius: 8, flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', position: 'relative' }}>
            <div ref={treeContentRef} style={{ display: 'flex', justifyContent: 'center', padding: 40, transformOrigin: 'center center', transform: `scale(${scale})`, transition: 'transform 0.2s ease-out' }}>
              <SvgTreeView faction={rootFaction} allFactions={relationshipFactions} currentFactionId={faction.id} onSelectFaction={(factionId) => { onSelectFaction(factionId); onClose(); }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============== 创建/编辑势力弹窗 ===============
export function FactionFormModal({ faction, factions, onClose, onSave, presetName }: {
  faction?: Faction; factions: Faction[]; onClose: () => void;
  onSave: (name: string, parentFactionId: string | null, description: string) => void | Promise<void>;
  /** 取名工具联动：预填名称 */
  presetName?: string;
}) {
  const [name, setName] = useState(faction?.name || presetName || '');
  const [parentFactionId, setParentFactionId] = useState<string | null>(faction?.parent_faction_id || null);
  const [description, setDescription] = useState(faction?.description || '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const otherFactions = factions.filter(f => f.id !== faction?.id);

  const checkNameUniqueness = (forceName: string) => {
    if (!forceName.trim()) { setError(''); return true; }
    if (otherFactions.some(f => f.name === forceName.trim())) { setError('该名称已存在，请重新命名'); return false; }
    setError(''); return true;
  };

  const handleSave = async () => {
    if (submittingRef.current) return;
    if (!name.trim()) { setError('势力名称不能为空'); return; }
    if (!checkNameUniqueness(name)) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onSave(name, parentFactionId, description);
    } catch {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const inputStyle = { width: '100%', padding: 10, marginBottom: 15, boxSizing: 'border-box' as const, border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-color)', color: 'var(--text-color)', borderRadius: 4, fontSize: 14 };
  return (
    <div style={FORM_MODAL_MASK}>
      <div style={{ backgroundColor: 'var(--card-bg)', padding: 20, borderRadius: 8, width: 400, boxShadow: '0 4px 6px var(--shadow-color)', color: 'var(--text-color)' }}>
        <h2 style={{ marginBottom: 20 }}>{faction ? '编辑势力' : '创建势力'}</h2>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="势力名称" style={inputStyle} />
        <select value={parentFactionId || ''} onChange={e => setParentFactionId(e.target.value || null)} style={inputStyle}>
          <option value="">无上级势力</option>
          {otherFactions.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="势力描述" style={{ ...inputStyle, height: 100, resize: 'none' }} />
        {error && <div style={{ color: 'var(--danger-color)', fontSize: 12, marginBottom: 15 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', border: '1px solid var(--border-color)', backgroundColor: 'var(--card-bg)', color: 'var(--text-color)', borderRadius: 4, cursor: 'pointer' }}>取消</button>
          <button onClick={handleSave} disabled={submitting} style={{ padding: '8px 16px', backgroundColor: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: 4, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1 }}>{submitting ? '保存中...' : '确定'}</button>
        </div>
      </div>
    </div>
  );
}

// =============== 创建/编辑人物弹窗 ===============
export function CharacterFormModal({ character, factions, characters, onClose, onSave, presetName }: {
  character?: Character; factions: Faction[]; characters: Character[]; onClose: () => void;
  onSave: (name: string, age: string | null, factionId: string | null, strength: string | null, description: string) => void | Promise<void>;
  /** 取名工具联动：预填名称 */
  presetName?: string;
}) {
  const [name, setName] = useState(character?.name || presetName || '');
  const [age, setAge] = useState(character?.age || '');
  const [factionId, setFactionId] = useState<string | null>(character?.faction_id || null);
  const [strength, setStrength] = useState(character?.strength || '');
  const [description, setDescription] = useState(character?.description || '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const otherCharacters = characters.filter(c => c.id !== character?.id);

  const checkNameUniqueness = (charName: string) => {
    if (!charName.trim()) { setError(''); return true; }
    if (otherCharacters.some(c => c.name === charName.trim())) { setError('该名称已存在，请重新命名'); return false; }
    setError(''); return true;
  };

  const handleSave = async () => {
    if (submittingRef.current) return;
    if (!name.trim()) { setError('人物名称不能为空'); return; }
    if (!checkNameUniqueness(name)) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onSave(name, age || null, factionId, strength || null, description);
    } catch {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const inputStyle = { width: '100%', padding: 10, marginBottom: 15, boxSizing: 'border-box' as const, border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-color)', color: 'var(--text-color)', borderRadius: 4, fontSize: 14 };
  return (
    <div style={FORM_MODAL_MASK}>
      <div style={{ backgroundColor: 'var(--card-bg)', padding: 20, borderRadius: 8, width: 500, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 4px 6px var(--shadow-color)', color: 'var(--text-color)', display: 'flex', flexDirection: 'column' }}>
        <h2 style={{ marginBottom: 20 }}>{character ? '编辑人物' : '创建人物'}</h2>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="人物名称 *" style={inputStyle} />
        <input value={age} onChange={e => setAge(e.target.value)} placeholder="年龄（可选）" style={inputStyle} />
        <select value={factionId || ''} onChange={e => setFactionId(e.target.value || null)} style={inputStyle}>
          <option value="">无所属势力</option>
          {factions.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <input value={strength} onChange={e => setStrength(e.target.value)} placeholder="实力（可选）" style={inputStyle} />
        <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="人物描述（可选）" style={{ ...inputStyle, flex: 1, minHeight: 100, resize: 'none' }} />
        {error && <div style={{ color: 'var(--danger-color)', fontSize: 12, marginBottom: 15 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', border: '1px solid var(--border-color)', backgroundColor: 'var(--card-bg)', color: 'var(--text-color)', borderRadius: 4, cursor: 'pointer' }}>取消</button>
          <button onClick={handleSave} disabled={submitting} style={{ padding: '8px 16px', backgroundColor: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: 4, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1 }}>{submitting ? '保存中...' : '确定'}</button>
        </div>
      </div>
    </div>
  );
}

// =============== 创建/编辑物品弹窗 ===============
export function ItemFormModal({ item, items, onClose, onSave, presetName, presetType }: {
  item?: Item; items: Item[]; onClose: () => void;
  onSave: (name: string, type: string, description: string) => void | Promise<void>;
  /** 取名工具联动：预填名称与类型 */
  presetName?: string;
  presetType?: string;
}) {
  const presetTypes = ['武器', '书籍', '功法', '草药'];
  const [name, setName] = useState(item?.name || presetName || '');
  const [type, setType] = useState(() => (item && presetTypes.includes(item.type)) ? item.type : (presetType && presetTypes.includes(presetType) ? presetType : ''));
  const [customType, setCustomType] = useState(() => (item && !presetTypes.includes(item.type)) ? item.type : (presetType && !presetTypes.includes(presetType) ? presetType : ''));
  const [description, setDescription] = useState(item?.description || '');
  const [isCustomType, setIsCustomType] = useState(() => (item ? !presetTypes.includes(item.type) : !!(presetType && !presetTypes.includes(presetType))));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const otherItems = items.filter(i => i.id !== item?.id);

  const checkNameUniqueness = (itemName: string) => {
    if (!itemName.trim()) { setError(''); return true; }
    if (otherItems.some(i => i.name === itemName.trim())) { setError('该名称已存在，请重新命名'); return false; }
    setError(''); return true;
  };

  const finalType = isCustomType ? customType : type;
  const handleSave = async () => {
    if (submittingRef.current) return;
    if (!name.trim()) { setError('物品名称不能为空'); return; }
    if (!finalType.trim()) { setError('物品类型不能为空'); return; }
    if (!checkNameUniqueness(name)) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onSave(name, finalType, description);
    } catch {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const inputStyle = { width: '100%', padding: 10, marginBottom: 15, boxSizing: 'border-box' as const, border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-color)', color: 'var(--text-color)', borderRadius: 4, fontSize: 14 };
  return (
    <div style={FORM_MODAL_MASK}>
      <div style={{ backgroundColor: 'var(--card-bg)', padding: 20, borderRadius: 8, width: 500, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 4px 6px var(--shadow-color)', color: 'var(--text-color)', display: 'flex', flexDirection: 'column' }}>
        <h2 style={{ marginBottom: 20 }}>{item ? '编辑物品' : '创建物品'}</h2>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="物品名称 *" style={inputStyle} />
        {!isCustomType ? (
          <select value={type} onChange={e => { if (e.target.value === 'custom') { setIsCustomType(true); setType(''); } else setType(e.target.value); }} style={inputStyle}>
            <option value="">选择物品类型</option>
            {presetTypes.map(t => <option key={t} value={t}>{t}</option>)}
            <option value="custom">其他</option>
          </select>
        ) : (
          <>
            <select value="custom" disabled style={inputStyle}>
              <option value="">选择物品类型</option>
              {presetTypes.map(t => <option key={t} value={t}>{t}</option>)}
              <option value="custom">其他</option>
            </select>
            <input value={customType} onChange={e => setCustomType(e.target.value)} placeholder="自定义物品类型 *" style={inputStyle} />
            <button onClick={() => { setIsCustomType(false); setCustomType(''); }} style={{ marginBottom: 15, padding: '8px 12px', fontSize: 12, backgroundColor: 'var(--text-secondary)', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', width: '100%' }}>回到预设列表</button>
          </>
        )}
        <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="物品描述（可选）" style={{ ...inputStyle, marginBottom: 20, flex: 1, minHeight: 100, resize: 'none' }} />
        {error && <div style={{ color: 'var(--danger-color)', fontSize: 12, marginBottom: 15 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', border: '1px solid var(--border-color)', backgroundColor: 'var(--card-bg)', color: 'var(--text-color)', borderRadius: 4, cursor: 'pointer' }}>取消</button>
          <button onClick={handleSave} disabled={submitting || !name.trim() || !finalType.trim()} style={{ padding: '8px 16px', backgroundColor: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: 4, cursor: (!submitting && name.trim() && finalType.trim()) ? 'pointer' : 'not-allowed', opacity: (!submitting && name.trim() && finalType.trim()) ? 1 : 0.5 }}>{submitting ? '保存中...' : '确定'}</button>
        </div>
      </div>
    </div>
  );
}

// =============== 主组件 ===============
/**
 * 资料库页（势力 / 人物 / 物品）。
 *
 * 标签栏**由本页自己持有状态并渲染**：这三项原本是侧边栏里的可展开子菜单，
 * 现改为侧边栏只做一级导航（普通按钮），二级切换挪进页面内。
 *
 * `initialTab` 只用于**外部跳转**（全局搜索命中某一项时直接落到对应标签），
 * 由调用方改这个值来驱动；用户点标签则只改内部 state。
 * 不采用"受控 `activeTab`"：那样内部 state 永远被外部值覆盖，标签实际点不动
 * （`controlledTab ?? internalTab`），此前是靠 `hideTabs` 把这一层不稳定掩盖掉了。
 */
export function DataLibrary({ initialTab }: { initialTab?: TabType } = {}) {
  const { id } = useParams();
  const bookId = id || '00000000-0000-0000-0000-000000000000';
  const [currentTab, setCurrentTab] = useState<TabType>(initialTab ?? 'factions');

  // 外部跳转改变了目标标签时跟随（用户自己点标签不会触发，因为只改内部 state）
  useEffect(() => {
    if (initialTab) setCurrentTab(initialTab);
  }, [initialTab]);

  const { dataRefreshTrigger, triggerDataRefresh } = useWorkspace();

  // ---- 按「容器宽度」决定单栏还是两栏（不能用 window.innerWidth）----
  // 资料库要作为创作页分屏工作台里的窄面板使用：宽度由用户拖拽分屏条决定（常见 240–460px），
  // 拖窄面板时视口宽度根本没变，用视口判断会得出「还很宽」的错误结论。
  // 所以这里用 ResizeObserver 量**本组件根元素**的宽度（分屏拖拽、窗口缩放、
  // 切换编辑器视图导致 display:none→flex，ResizeObserver 都会重新回报）。
  // 断点值与取值理由见 dataLibraryLayout.ts。
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const narrow = resolveDataLayout(containerWidth).narrow;

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    // 用 clientWidth 而不是 getBoundingClientRect().width：前者是**布局宽度**，
    // 不受祖先 transform（如编辑器入场的 fadeUp / 缩放）影响，与 ResizeObserver
    // 回报的尺寸口径一致；根节点没有边框和内边距，两者数值本就相同。
    const measure = () => setContainerWidth(el.clientWidth);
    // useLayoutEffect 在浏览器绘制前跑：首帧就量到真实宽度，不会先画一帧两栏再跳成单栏
    measure();
    if (typeof ResizeObserver === 'undefined') {
      // 兜底：没有 ResizeObserver 的环境退化为监听窗口尺寸（分屏拖拽的实时跟随会失效）
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const [factions, setFactions] = useState<Faction[]>(() => dataCache.getFactions(bookId) ?? []);
  const [showFactionForm, setShowFactionForm] = useState(false);
  const [showFactionRelationship, setShowFactionRelationship] = useState(false);
  const [editingFaction, setEditingFaction] = useState<Faction | null>(null);

  const [characters, setCharacters] = useState<Character[]>(() => dataCache.getCharacters(bookId) ?? []);
  const [showCharacterForm, setShowCharacterForm] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<Character | null>(null);

  const [items, setItems] = useState<Item[]>(() => dataCache.getItems(bookId) ?? []);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);

  const [selectedDetail, setSelectedDetail] = useState<Faction | Character | Item | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [cardListOpen, setCardListOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editData, setEditData] = useState<Record<string, string | number | null>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ show: boolean; title: string; message: string; onConfirm: () => void } | null>(null);
  // FLIP 快照：打开/关闭时在 React 提交后、浏览器绘制前冻结卡片旧位置，避免闪现新布局。
  // releaseDelay > 0 时（关闭方向）延迟释放，等详情面板先滑出后再让卡片展开归位。
  const flipRef = useRef<{ cards: HTMLElement[]; first: DOMRect[]; releaseDelay: number } | null>(null);
  const flipReleaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    const pending = flipRef.current;
    if (!pending) return;
    flipRef.current = null;
    const { cards, first, releaseDelay } = pending;
    let hasMotion = false;
    cards.forEach((card, i) => {
      const last = card.getBoundingClientRect();
      const dx = first[i].left - last.left, dy = first[i].top - last.top;
      // 宽度也一并冻结（scaleX）：卡片边缩小边移动，而不是先变窄再飞入
      const sx = first[i].width / last.width;
      if (dx || dy || Math.abs(sx - 1) > 0.01) {
        hasMotion = true;
        // 只冻结位置/尺寸（transform 0s 立即生效），保留内容属性（padding/高度等）的过渡，形变全程平滑
        card.style.transition = 'transform 0s, padding 0.8s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.8s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.76s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease, margin 0.76s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.5s ease, border-color 0.5s ease, box-shadow 0.5s ease';
        card.style.transform = `translate(${dx}px, ${dy}px) scaleX(${sx})`;
      }
    });
    if (!hasMotion) return;
    // 强制同步布局：冻结状态成为本帧的绘制内容，浏览器不会先画出未冻结的新布局
    void document.body.offsetWidth;
    const release = () => {
      cards.forEach((card) => {
        // 释放：位置/宽度与内容形变同时过渡，统一快起步缓动（一开始就在缩小且移动）
        card.style.transition = 'transform 0.76s cubic-bezier(0.25, 0.46, 0.45, 0.94), padding 0.76s cubic-bezier(0.25, 0.46, 0.45, 0.94), border-radius 0.76s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-height 0.76s cubic-bezier(0.25, 0.46, 0.45, 0.94), font-size 0.76s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.6s ease, margin 0.76s cubic-bezier(0.25, 0.46, 0.45, 0.94), background-color 0.5s ease, border-color 0.5s ease, box-shadow 0.5s ease';
        card.style.transform = '';
      });
      if (flipReleaseTimerRef.current) clearTimeout(flipReleaseTimerRef.current);
      // 动画结束后彻底清理内联样式，恢复样式表过渡（hover 等不再被打断/残留）
      flipReleaseTimerRef.current = setTimeout(() => {
        cards.forEach((card) => {
          card.style.transition = '';
          card.style.transform = '';
        });
        flipReleaseTimerRef.current = null;
      }, 900);
    };
    if (releaseDelay > 0) {
      setTimeout(release, releaseDelay);
    } else {
      requestAnimationFrame(release);
    }
  });

  const factionRef = useRef<HTMLDivElement>(null);
  const characterRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const getActiveContainer = () => {
    if (currentTab === 'factions') return factionRef.current;
    if (currentTab === 'characters') return characterRef.current;
    return itemRef.current;
  };

  /**
   * 每个标签页自己的两栏容器类名（三个标签共用，避免三处手写串出错）。
   *
   * - `detail-open`：详情面板滑入。宽栏=右侧 68% 面板滑入；窄栏=同一条面板铺满整栏
   *   盖住列表（宽度差异在 dataLibrary.css 里按 `.is-narrow` 区分）。
   * - `card-list`：宽栏下把卡片网格压成左侧固定 227px 的列表条。
   *   **窄栏不加这个类**：窄栏的列表本来就是整栏单列，详情是整栏覆盖，
   *   没有"压成列表条"这一步 —— 而且 240px 的面板里塞 227px 固定宽会横向溢出。
   */
  const containerClass = (tab: TabType) => 'data-library-container'
    + (detailOpen && currentTab === tab ? ' detail-open' : '')
    + (cardListOpen && currentTab === tab && !narrow ? ' card-list' : '');

  const [loadFailed, setLoadFailed] = useState(false);

  const loadData = async () => {
    try {
      const [fd, cd, id2] = await Promise.all([window.api.getFactions(bookId), window.api.getCharacters(bookId), window.api.getItems(bookId)]);
      dataCache.setFactions(bookId, fd);
      dataCache.setCharacters(bookId, cd);
      dataCache.setItems(bookId, id2);
      setFactions(fd); setCharacters(cd); setItems(id2);
      setLoadFailed(false);
    } catch (err) {
      // 此前这里没有 catch：失败时保持初值，界面显示"暂无势力记录" —— 与"确实没有"无法区分
      setLoadFailed(true);
      notifyError('资料库加载失败：' + errorMessage(err));
    }
  };
  useEffect(() => { void loadData(); }, [bookId, dataRefreshTrigger]);
  useLayoutEffect(() => { if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; } setDetailOpen(false); setCardListOpen(false); setSelectedDetail(null); setIsDirty(false); }, [currentTab]);


  const initEditData = (entity: Faction | Character | Item) => {
    const d: Record<string, string | number | null> = {};
    if (currentTab === 'factions') {
      d.name = (entity as Faction).name;
      d.parent_faction_id = (entity as Faction).parent_faction_id;
      d.description = (entity as Faction).description ?? '';
    } else if (currentTab === 'characters') {
      d.name = (entity as Character).name;
      d.age = (entity as Character).age ?? '';
      d.strength = (entity as Character).strength ?? '';
      d.faction_id = (entity as Character).faction_id;
      d.description = (entity as Character).description ?? '';
    } else {
      d.name = (entity as Item).name;
      d.type = (entity as Item).type;
      d.description = (entity as Item).description ?? '';
    }
    setEditData(d); setIsDirty(false);
  };

  /**
   * FLIP 第一步：冻结卡片"旧位置"快照（宽栏两栏动画用）。
   *
   * 窄栏（单栏「列表 ⇄ 详情」）**显式跳过**：详情是整栏铺满、把列表整个盖住，
   * 卡片既不缩进左侧列表条也不改变尺寸，`first === last` 没有任何位移可做，
   * 记录快照纯属白算。窄栏的转场由详情面板自身的滑入/滑出承担（见 dataLibrary.css）。
   * 宽栏下这里与改动前逐字一致，两栏 FLIP 不受影响。
   */
  const captureCardFlip = (): void => {
    if (narrow) { flipRef.current = null; return; }
    const container = getActiveContainer();
    const cards = container ? Array.from(container.querySelectorAll<HTMLElement>('.data-card')) : [];
    flipRef.current = { cards, first: cards.map(c => c.getBoundingClientRect()), releaseDelay: 0 };
  };

  const openDetail = (entity: Faction | Character | Item) => {
    captureCardFlip();
    setSelectedDetail(entity); initEditData(entity); setDetailOpen(true); setCardListOpen(true);
  };

  const doCloseDetail = () => {
    // 关闭：面板与卡片同时收场（next 帧即释放，无延迟）
    captureCardFlip();
    setDetailOpen(false); setCardListOpen(false); setIsDirty(false); setSelectedDetail(null);
  };

  const closeDetail = () => {
    if (isDirty) { setConfirmModal({ show: true, title: '未保存的更改', message: '你有未保存的更改，确定要放弃吗？', onConfirm: () => { setConfirmModal(null); doCloseDetail(); }}); return; }
    doCloseDetail();
  };

  const handleTabClick = (tab: TabType) => {
    if (isDirty) { setConfirmModal({ show: true, title: '未保存的更改', message: '你有未保存的更改，确定要放弃吗？', onConfirm: () => { setDetailOpen(false); setCardListOpen(false); setConfirmModal(null); setIsDirty(false); setSelectedDetail(null); setCurrentTab(tab); }}); return; }
    setCurrentTab(tab);
  };

  /** 统一的保存/删除错误处理：失败时给出可见提示，不静默吞掉（如名称重复 409） */
  const runGuarded = async (fn: () => Promise<unknown>, success?: () => void) => {
    try {
      await fn();
      success?.();
    } catch (err) {
      notifyError(errorMessage(err));
    }
  };

  const handleSave = () => {
    if (!selectedDetail) return;
    if (currentTab === 'factions') {
      void runGuarded(() => window.api.updateFaction({ id: (selectedDetail as Faction).id, name: String(editData.name || ''), parentFactionId: editData.parent_faction_id as string | null ?? null, description: String(editData.description || '') }));
    } else if (currentTab === 'characters') {
      void runGuarded(() => window.api.updateCharacter({ id: (selectedDetail as Character).id, name: String(editData.name || ''), age: String(editData.age || '') || null, factionId: (editData.faction_id as string) ?? null, strength: String(editData.strength || '') || null, description: String(editData.description || '') }));
    } else {
      void runGuarded(() => window.api.updateItem({ id: (selectedDetail as Item).id, name: String(editData.name || ''), type: String(editData.type || ''), description: String(editData.description || '') }));
    }
  };

  const handleDelete = () => {
    if (!selectedDetail) return;
    const label = currentTab === 'factions' ? '势力' : currentTab === 'characters' ? '人物' : '物品';
    setConfirmModal({ show: true, title: `删除${label}`, message: `确定要删除该${label}吗？此操作不可撤销。`, onConfirm: () => {
      const deletedId = (selectedDetail as { id: string }).id;
      const list = currentTab === 'factions' ? factions : currentTab === 'characters' ? characters : items;
      const idx = list.findIndex(x => x.id === deletedId);
      // 顺序打开下一条；若删除的是最后一条则回退到前一条；删光后关闭面板
      const next = idx >= 0 ? (list[idx + 1] ?? list[idx - 1] ?? null) : null;
      const doDelete = () => {
        if (currentTab === 'factions') return window.api.deleteFaction((selectedDetail as Faction).id);
        if (currentTab === 'characters') return window.api.deleteCharacter((selectedDetail as Character).id);
        return window.api.deleteItem((selectedDetail as Item).id);
      };
      void runGuarded(doDelete, () => {
        setConfirmModal(null); setIsDirty(false);
        void loadData().then(() => {
          // 用刷新后的数据定位下一条（dataCache 已由 loadData 更新，避免使用陈旧对象）
          const freshList = currentTab === 'factions'
            ? (dataCache.getFactions(bookId) ?? [])
            : currentTab === 'characters'
              ? (dataCache.getCharacters(bookId) ?? [])
              : (dataCache.getItems(bookId) ?? []);
          const freshNext = next ? freshList.find(x => x.id === next.id) ?? null : null;
          if (freshNext) {
            setSelectedDetail(freshNext);
            initEditData(freshNext);
            setDetailOpen(true);
            setCardListOpen(true);
          } else {
            setSelectedDetail(null);
            setDetailOpen(false);
            setCardListOpen(false);
          }
          triggerDataRefresh();
        });
      });
    }});
  };

  const handleCreateFaction = (name: string, parentFactionId: string | null, description: string) => {
    void runGuarded(() => window.api.createFaction({ bookId, name, parentFactionId, description }), () => { setShowFactionForm(false); void loadData(); triggerDataRefresh(); });
  };
  const handleUpdateFaction = (name: string, parentFactionId: string | null, description: string) => {
    if (!editingFaction) return;
    void runGuarded(() => window.api.updateFaction({ id: editingFaction.id, name, parentFactionId, description }), () => { setEditingFaction(null); setShowFactionForm(false); void loadData(); triggerDataRefresh(); });
  };
  const handleCreateCharacter = (name: string, age: string | null, factionId: string | null, strength: string | null, description: string) => {
    void runGuarded(() => window.api.createCharacter({ bookId, name, age, factionId, strength, description }), () => { setShowCharacterForm(false); void loadData(); triggerDataRefresh(); });
  };
  const handleUpdateCharacter = (name: string, age: string | null, factionId: string | null, strength: string | null, description: string) => {
    if (!editingCharacter) return;
    void runGuarded(() => window.api.updateCharacter({ id: editingCharacter.id, name, age, factionId, strength, description }), () => { setEditingCharacter(null); setShowCharacterForm(false); void loadData(); triggerDataRefresh(); });
  };
  const handleCreateItem = (name: string, type: string, description: string) => {
    void runGuarded(() => window.api.createItem({ bookId, name, type, description }), () => { setShowItemForm(false); void loadData(); triggerDataRefresh(); });
  };
  const handleUpdateItem = (name: string, type: string, description: string) => {
    if (!editingItem) return;
    void runGuarded(() => window.api.updateItem({ id: editingItem.id, name, type, description }), () => { setEditingItem(null); setShowItemForm(false); void loadData(); triggerDataRefresh(); });
  };

  const inputStyle: React.CSSProperties = { width:'100%', padding:'8px 10px', border:'1px solid var(--border-color)', backgroundColor:'var(--bg-color)', color:'var(--text-color)', borderRadius:4, fontSize:13, boxSizing:'border-box', outline:'none' };
  const textareaStyle: React.CSSProperties = { ...inputStyle, resize:'none', lineHeight:1.5, height:120, overflowY:'auto' };

  const handleEditChange = (key: string, value: string) => {
    setEditData(prev => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const renderDetailActions = () => (
    <div className="data-detail-actions">
      <button className="form-button" style={{ backgroundColor: 'var(--primary-color)' }} onClick={handleSave} disabled={!isDirty}>{isDirty ? '保存 *' : '保存'}</button>
      <button className="form-button" style={{ backgroundColor: 'var(--danger-color)' }} onClick={handleDelete}>删除</button>
      <button className="form-button" style={{ backgroundColor: 'var(--text-secondary)' }} onClick={closeDetail}>关闭</button>
    </div>
  );

  const renderDetailContent = (header: React.ReactNode, extra: React.ReactNode) => (
    <>
      <div className="data-detail-panel-header">
        {/* 窄栏单栏模式：详情是整栏覆盖，必须给一个显式的「返回列表」出口。
            宽栏是两栏并存（列表仍在左边），不需要这个按钮，DOM 与改动前一致。
            走 closeDetail 而不是直接关：未保存时同样弹「放弃更改」确认。 */}
        {narrow && (
          <button
            type="button"
            className="data-detail-back"
            onClick={closeDetail}
            title="返回列表"
            aria-label="返回列表"
            data-testid="data-detail-back"
          >
            <ArrowLeft size={12} aria-hidden />
            返回列表
          </button>
        )}
        {header}
      </div>
      <div className="data-detail-panel-body">
        <div style={{ display:'flex', flexDirection:'column', gap:16, height:'100%' }}>
          <div style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column', gap:16 }}>
            {extra}
          </div>
          {renderDetailActions()}
        </div>
      </div>
    </>
  );

  const commonInput = (label: string, key: string) => (
    <div className="data-detail-field">
      <div className="data-detail-field-label">{label}</div>
      <input style={inputStyle} value={String(editData[key] ?? '')} onChange={e => handleEditChange(key, e.target.value)} />
    </div>
  );
  const commonTextarea = (label: string, key: string) => (
    <div className="data-detail-field" style={{ flex:1, display:'flex', flexDirection:'column', minHeight:0 }}>
      <div className="data-detail-field-label">{label}</div>
      <textarea style={{ ...textareaStyle, flex:1, minHeight:80, height:'auto', width:'100%', resize:'none', overflowY:'auto' }} value={String(editData[key] ?? '')} onChange={e => handleEditChange(key, e.target.value)} />
    </div>
  );
  const commonSelect = (label: string, key: string, options: { value: number | string; label: string }[], emptyLabel?: string) => (
    <div className="data-detail-field">
      <div className="data-detail-field-label">{label}</div>
      <select style={{ ...inputStyle, height:36 }} value={String(editData[key] ?? '')} onChange={e => handleEditChange(key, e.target.value === '__none__' ? '' : e.target.value)}>
        {emptyLabel && <option value="__none__">{emptyLabel}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  const renderFactionDetail = () => {
    if (!selectedDetail) return null;
    const faction = selectedDetail as Faction;
    const parentFaction = factions.find(f => f.id === faction.parent_faction_id);
    return renderDetailContent(
      <h2 style={{ margin:0, fontSize:18, fontWeight:600 }}>{faction.name}</h2>,
      <>
        {commonInput('名称', 'name')}
        {commonSelect('上级势力', 'parent_faction_id', factions.filter(f => f.id !== faction.id).map(f => ({ value: f.id, label: f.name })), '无上级势力')}
        {parentFaction && <div className="data-detail-field"><div className="data-detail-field-label">当前上级势力</div><div className="data-detail-field-value" style={{ color:'var(--primary-color)', fontWeight:600 }}>{parentFaction.name}</div></div>}
        <div><button className="form-button" onClick={() => setShowFactionRelationship(true)}>查看关系树</button></div>
        {commonTextarea('势力描述', 'description')}
      </>
    );
  };

  const renderCharacterDetail = () => {
    if (!selectedDetail) return null;
    return renderDetailContent(
      <h2 style={{ margin:0, fontSize:18, fontWeight:600 }}>{(selectedDetail as Character).name}</h2>,
      <>
        {commonInput('名称', 'name')}
        {/* 两列表单网格：窄栏折成单列（见 dataLibrary.css 的 .is-narrow 段） */}
        <div className="data-detail-grid-2">
          {commonInput('年龄', 'age')}
          {commonInput('实力', 'strength')}
        </div>
        {commonSelect('所属势力', 'faction_id', factions.map(f => ({ value: f.id, label: f.name })), '无所属势力')}
        {commonTextarea('人物描述', 'description')}
        {/* 人物关系与出场反查（复用全书检索能力） */}
        <CharacterRelationsPanel
          bookId={bookId}
          character={selectedDetail as Character}
          allCharacters={characters}
        />
      </>
    );
  };

  const renderItemDetail = () => {
    if (!selectedDetail) return null;
    return renderDetailContent(
      <h2 style={{ margin:0, fontSize:18, fontWeight:600 }}>{(selectedDetail as Item).name}</h2>,
      <>
        {commonInput('名称', 'name')}
        {commonInput('类型', 'type')}
        {commonTextarea('物品描述', 'description')}
      </>
    );
  };

  const renderEmptyHint = (icon: string, label: string, desc: string) => (
    <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '64px 16px', gap: 6 }}>
      <div style={{ fontSize: 40, marginBottom: 8, opacity: 0.7 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-color)' }}>{label}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{desc}</div>
    </div>
  );

  return (
    // rootRef：ResizeObserver 量这个元素（面板根）的宽度 —— 布局断点只认容器宽度，不认视口。
    // is-narrow：窄容器的全部样式开关（见 dataLibrary.css 末尾的窄栏段）。
    // 页面头部/标签栏的样式搬进了 CSS 类（内联样式优先级高于样式表，窄栏调内边距覆盖不掉）。
    <div
      ref={rootRef}
      className={`data-library-root${narrow ? ' is-narrow' : ''}${detailOpen ? ' is-detail-open' : ''}`}
      data-layout={narrow ? 'narrow' : 'wide'}
      style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--bg-color)', color: 'var(--text-color)' }}
    >
      <div className="data-library-page-header">
        <div className="data-library-header-meta">
          <span className="data-library-header-title">{currentTab === 'factions' ? '势力' : currentTab === 'characters' ? '人物' : '物品'}</span>
          <span className="data-library-header-count">{currentTab === 'factions' ? factions.length : currentTab === 'characters' ? characters.length : items.length} 条</span>
        </div>
        <button className="data-library-add-button" onClick={() => { if (currentTab === 'factions') setShowFactionForm(true); else if (currentTab === 'characters') setShowCharacterForm(true); else setShowItemForm(true); }}>{currentTab === 'factions' ? '新增势力' : currentTab === 'characters' ? '新增人物' : '新增物品'}</button>
      </div>
      {/* 标签栏：状态由页面自己持有（currentTab），这里是普通 onClick，不走受控 props */}
      <div className="data-library-tabbar">
        {(['factions', 'characters', 'items'] as TabType[]).map(tab => (
          <div
            key={tab}
            role="tab"
            tabIndex={0}
            aria-selected={currentTab === tab}
            data-tab={tab}
            className={`data-library-tab${currentTab === tab ? ' is-active' : ''}`}
            onClick={() => handleTabClick(tab)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTabClick(tab); } }}
          >
            {tab==='factions'?'势力':tab==='characters'?'人物':'物品'}
            {currentTab===tab && <div style={{ position:'absolute', bottom:0, left:0, right:0, height:2, backgroundColor:'var(--primary-color)', borderRadius:'2px 2px 0 0' }} />}
          </div>
        ))}
      </div>

      <div style={{ flex:1, display:currentTab==='factions'?'flex':'none', overflow:'hidden', minHeight:0 }}>
         <div ref={factionRef} className={containerClass('factions')}>
          <div className="data-card-area">
            {factions.map(f => (<div key={f.id} className={`data-card force-card${selectedDetail&&currentTab==='factions'&&(selectedDetail as Faction).id===f.id?' selected':''}`} onClick={()=>openDetail(f)}><h3 className="data-card-title">{f.name}</h3><div className="data-card-meta">{'↳ 属于：'}{factions.find(x=>x.id===f.parent_faction_id)?.name||'无上级势力'}</div><p className="data-card-description">{f.description||'暂无描述'}</p></div>))}
            {factions.length===0&&(loadFailed?<LoadError message="资料库加载失败" onRetry={()=>{void loadData();}} compact/>:renderEmptyHint('🛡️', '暂无势力记录', '点击右上角「新增势力」，构建你的世界观'))}
          </div>
          <div className="data-detail-panel">{selectedDetail && currentTab === 'factions' && renderFactionDetail()}</div>
        </div>
      </div>

      <div style={{ flex:1, display:currentTab==='characters'?'flex':'none', overflow:'hidden', minHeight:0 }}>
          <div ref={characterRef} className={containerClass('characters')}>
          <div className="data-card-area">
            {characters.map(ch => (<div key={ch.id} className={`data-card character-card${selectedDetail&&currentTab==='characters'&&(selectedDetail as Character).id===ch.id?' selected':''}`} onClick={()=>openDetail(ch)}><h3 className="data-card-title">{ch.name}</h3><div className="data-card-meta">{'↳ 属于：'}{factions.find(f=>f.id===ch.faction_id)?.name||'无所属势力'}</div><div className="character-info">{ch.age&&<div>年龄: {ch.age}</div>}{ch.strength&&<div>实力: {ch.strength}</div>}</div><p className="data-card-description">{ch.description||'暂无描述'}</p></div>))}
            {characters.length===0&&(loadFailed?<LoadError message="资料库加载失败" onRetry={()=>{void loadData();}} compact/>:renderEmptyHint('👤', '暂无人物记录', '点击右上角「新增人物」，记录故事中的角色'))}
          </div>
          <div className="data-detail-panel">{selectedDetail && currentTab === 'characters' && renderCharacterDetail()}</div>
        </div>
      </div>

      <div style={{ flex:1, display:currentTab==='items'?'flex':'none', overflow:'hidden', minHeight:0 }}>
          <div ref={itemRef} className={containerClass('items')}>
          <div className="data-card-area">
            {items.map(it => (<div key={it.id} className={`data-card item-card${selectedDetail&&currentTab==='items'&&(selectedDetail as Item).id===it.id?' selected':''}`} onClick={()=>openDetail(it)}><h3 className="data-card-title">{it.name}</h3><div className="data-card-type"><span className="type-badge">{it.type}</span></div><p className="data-card-description">{it.description||'暂无描述'}</p></div>))}
            {items.length===0&&(loadFailed?<LoadError message="资料库加载失败" onRetry={()=>{void loadData();}} compact/>:renderEmptyHint('📦', '暂无物品记录', '点击右上角「新增物品」，收集剧情道具'))}
          </div>
          <div className="data-detail-panel">{selectedDetail && currentTab === 'items' && renderItemDetail()}</div>
        </div>
      </div>

      {showFactionRelationship && selectedDetail && currentTab === 'factions' && <FactionRelationshipModal faction={selectedDetail as Faction} allFactions={factions} onClose={() => setShowFactionRelationship(false)} onSelectFaction={(fid: string) => { const t = factions.find(f => f.id === fid); if (t) setSelectedDetail(t); }} />}
      {showFactionForm && <FactionFormModal faction={editingFaction || undefined} factions={factions} onClose={() => { setShowFactionForm(false); setEditingFaction(null); }} onSave={editingFaction ? handleUpdateFaction : handleCreateFaction} />}
      {showCharacterForm && <CharacterFormModal character={editingCharacter || undefined} factions={factions} characters={characters} onClose={() => { setShowCharacterForm(false); setEditingCharacter(null); }} onSave={editingCharacter ? handleUpdateCharacter : handleCreateCharacter} />}
      {showItemForm && <ItemFormModal item={editingItem || undefined} items={items} onClose={() => { setShowItemForm(false); setEditingItem(null); }} onSave={editingItem ? handleUpdateItem : handleCreateItem} />}
      {confirmModal && <GlobalConfirmModal show={confirmModal.show} title={confirmModal.title} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onCancel={() => setConfirmModal(null)} />}
    </div>
  );
}
